import { type intlFormatPropTypes } from "react-intl";

import {
  createVatAddress,
  updateVatProfile,
} from "../../../app-main/actions/fee-profile";
import { TAX_DETAILS_VAT_PROFILE_TYPE } from "../../TaxDetails/TaxDetails.consts";
import { TAX_DETAILS_FORM_RETURN_PATH } from "../TaxDetailsFormSection.consts";
import { TAX_DETAILS_FORM_ERROR_MESSAGES } from "../TaxDetailsFormSection.messages";
import type {
  TaxDetailsFormSubmitResult,
  TaxDetailsFormValues,
} from "../TaxDetailsFormSection.types";
import { compactTaxId } from "../TaxDetailsFormSection.utils";

// The store is still plain JS, so there's no canonical `AppDispatch` to import.
// Both actions are thunks that resolve or reject with the API response.
type Dispatch = (action: unknown) => Promise<unknown>;

type SubmitTaxDetailsFormArgs = {
  accessToken: string;
  dispatch: Dispatch;
  intl: Pick<intlFormatPropTypes, "formatMessage">;
  /** Only a host declares a rate, so only a host's payload carries one. */
  isHost: boolean;
  isSubregion: boolean;
  /** The profile to update; absent when adding a registration. */
  registrationId?: string;
  ssoId: string;
  values: TaxDetailsFormValues;
};

/**
 * The form's values as the Fees API expects them. `isConfirmed` is deliberately
 * absent: it gates submit and means nothing once the registration is stored.
 */
const buildVatProfilePayload = ({
  isHost,
  isSubregion,
  ssoId,
  values,
}: Pick<
  SubmitTaxDetailsFormArgs,
  "isHost" | "isSubregion" | "ssoId" | "values"
>) => ({
  business_name: values.businessName,
  vat_id: compactTaxId({
    country: values.country,
    isSubregion,
    state: values.state,
    taxId: values.taxId,
  }),
  country: values.country,
  street: values.street,
  unit: values.unit,
  city: values.city,
  state: values.state,
  zip: values.zip,
  type: TAX_DETAILS_VAT_PROFILE_TYPE,
  sso_id: ssoId,
  // Stored as a fraction, entered as a percentage.
  ...(isHost && { vat_percent: Number(values.taxRate) / 100 }),
  ...(isSubregion && { is_subregion: true }),
});

/**
 * Both actions reject with the API's own `error` string, so a server-side
 * validation message reaches the guest verbatim. Anything else is a failure we
 * cannot explain, and gets generic wording.
 */
const getErrorMessage = (
  reason: unknown,
  intl: SubmitTaxDetailsFormArgs["intl"],
): string =>
  "string" === typeof reason && 0 < reason.length
    ? reason
    : intl.formatMessage(TAX_DETAILS_FORM_ERROR_MESSAGES.submitFailed);

/**
 * Creates or updates the guest's VAT profile. Returns a result rather than
 * throwing, so the caller can hand the message to `form.setError`. On success the
 * browser navigates and the return value is never observed.
 */
const submitTaxDetailsForm = ({
  accessToken,
  dispatch,
  intl,
  isHost,
  isSubregion,
  registrationId,
  ssoId,
  values,
}: SubmitTaxDetailsFormArgs): Promise<TaxDetailsFormSubmitResult> => {
  const payload = buildVatProfilePayload({
    isHost,
    isSubregion,
    ssoId,
    values,
  });

  const request = registrationId
    ? updateVatProfile(accessToken, ssoId, registrationId, payload)
    : createVatAddress(accessToken, ssoId, payload);

  return dispatch(request).then(
    (): TaxDetailsFormSubmitResult => {
      window.location.href = TAX_DETAILS_FORM_RETURN_PATH;

      return { status: "success" };
    },
    (reason: unknown): TaxDetailsFormSubmitResult => ({
      status: "error",
      message: getErrorMessage(reason, intl),
    }),
  );
};

export { buildVatProfilePayload, submitTaxDetailsForm };

export type { SubmitTaxDetailsFormArgs };
